import { useState } from "react";
import type { ReactNode } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GroupIcon from "@mui/icons-material/Group";
import HistoryIcon from "@mui/icons-material/History";
import InboxIcon from "@mui/icons-material/Inbox";
import PublicIcon from "@mui/icons-material/Public";
import SensorsIcon from "@mui/icons-material/Sensors";
import SettingsIcon from "@mui/icons-material/Settings";
import StorageIcon from "@mui/icons-material/Storage";
import { useAuth } from "../auth/AuthProvider";
import type { Role } from "../api/types";
import { ChangePasswordDialog } from "./ChangePasswordDialog";

const DRAWER_WIDTH = 224;

interface NavEntry {
  to: string;
  label: string;
  icon: ReactNode;
  /** Hidden below this role — the API enforces the same boundary. */
  needs?: Role;
  group: "Triage" | "Configuration" | "Server" | "Access";
}

const NAV: NavEntry[] = [
  { to: "/", label: "Dashboard", icon: <DashboardIcon />, group: "Triage" },
  { to: "/leads", label: "Leads", icon: <InboxIcon />, group: "Triage" },
  { to: "/outcomes", label: "Outcomes", icon: <FactCheckIcon />, group: "Triage" },
  { to: "/sources", label: "Data sources", icon: <StorageIcon />, group: "Configuration" },
  { to: "/signals", label: "Signals", icon: <SensorsIcon />, group: "Configuration" },
  { to: "/markets", label: "Markets", icon: <PublicIcon />, group: "Configuration" },
  { to: "/clients", label: "Clients", icon: <GroupIcon />, group: "Configuration" },
  // Its own group, above Access: the email sender is deployment-wide state, not a
  // client's config and not an identity. Visible to every role because "which
  // mailbox do leads come from" is a question a viewer has to be able to answer;
  // the buttons on the screen are owner-gated.
  { to: "/settings", label: "Settings", icon: <SettingsIcon />, group: "Server" },
  { to: "/audit", label: "Audit log", icon: <HistoryIcon />, needs: "operator", group: "Access" },
  { to: "/admins", label: "Admin access", icon: <AssignmentIndIcon />, needs: "owner", group: "Access" },
];

const GROUPS = ["Triage", "Configuration", "Server", "Access"] as const;

export function AppLayout() {
  const { user, signOut, can } = useAuth();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const handleSignOut = async () => {
    setMenuAnchor(null);
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>
            FieldClients
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            Operations console
          </Typography>
          <Chip label={user?.role ?? "—"} color={user?.role === "owner" ? "primary" : "default"} variant="outlined" />
          <Button
            startIcon={<AccountCircleIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            color="inherit"
          >
            {user?.email ?? "…"}
          </Button>
          <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setPasswordOpen(true);
              }}
            >
              Change password
            </MenuItem>
            <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        <Toolbar variant="dense" />
        <Divider />
        <List dense sx={{ py: 0 }}>
          {GROUPS.map((group) => {
            const entries = NAV.filter((n) => n.group === group && (!n.needs || can(n.needs)));
            if (entries.length === 0) return null;
            return (
              <Box key={group}>
                <ListSubheader disableSticky sx={{ bgcolor: "transparent", lineHeight: "32px" }}>
                  {group}
                </ListSubheader>
                {entries.map((entry) => (
                  <ListItemButton
                    key={entry.to}
                    component={NavLink}
                    to={entry.to}
                    end={entry.to === "/"}
                    sx={{
                      "&.active": { bgcolor: "action.selected", borderLeft: 2, borderColor: "primary.main" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34 }}>{entry.icon}</ListItemIcon>
                    <ListItemText primary={entry.label} />
                  </ListItemButton>
                ))}
              </Box>
            );
          })}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
        <Toolbar variant="dense" />
        <Outlet />
      </Box>

      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </Box>
  );
}
